import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import styled from "styled-components";
import SEO from "../../components/seo/SEO";

const ReturnPolicyStyle = styled.div`
  padding: 60px 0;
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    gap: 42px;
  }
  .sidebar {
    width: 250px;
    position: sticky;
    top: 100px;
    height: fit-content;
    .sidebar__item {
      padding: 12px 16px;
      color: #303030;
      font-size: 15px;
      font-weight: 400;
      line-height: 22.5px;
      cursor: pointer;
      border-left: 3px solid transparent;
      transition: all 0.3s;
      &:hover {
        color: #ae0000;
        background: #fcfcfc;
      }
      &.active {
        color: #ae0000;
        font-weight: 600;
        border-left-color: #ae0000;
        background: #fcfcfc;
      }
    }
  }
  .content {
    flex: 1;
    h1 {
      color: #303030;
      font-size: 42px;
      font-weight: 700;
      line-height: 51px;
      margin-bottom: 30px;
    }
    .section {
      margin-bottom: 60px;
      scroll-margin-top: 100px;
      h2 {
        color: #303030;
        font-size: 27px;
        font-weight: 600;
        line-height: 34.5px;
        margin-bottom: 20px;
        margin-top: 40px;
      }
      h3 {
        color: #303030;
        font-size: 20px;
        font-weight: 600;
        line-height: 28px;
        margin-bottom: 15px;
        margin-top: 30px;
      }
      p {
        color: #303030;
        font-size: 15px;
        font-weight: 400;
        line-height: 22.5px;
        margin-bottom: 20px;
      }
      ul, ol {
        color: #303030;
        font-size: 15px;
        font-weight: 400;
        line-height: 22.5px;
        margin-bottom: 20px;
        padding-left: 20px;
        li {
          margin-bottom: 10px;
        }
      }
      strong {
        font-weight: 600;
      }
    }
  }
  @media (max-width: 1024px) {
    .container {
      flex-direction: column;
    }
    .sidebar {
      width: 100%;
      position: relative;
      top: 0;
      display: flex;
      overflow-x: auto;
      gap: 0;
      padding-bottom: 10px;
      .sidebar__item {
        white-space: nowrap;
        border-left: none;
        border-bottom: 3px solid transparent;
        &.active {
          border-left: none;
          border-bottom-color: #ae0000;
        }
      }
    }
  }
`;

// Helper function to get section ID
const getSectionId = (text) => {
  const sectionIdMap = {
    "return-eligibility-&-timeframe": "return-eligibility-timeframe",
    "return-shipping-costs": "return-shipping-costs",
    "restocking-fees": "restocking-fees",
    "damaged-or-incorrect-items": "damaged-or-incorrect-items",
    "final-sale-&-custom-orders": "final-sale-custom-orders",
    "refund-processing": "refund-processing",
    "who-handles-returns?": "who-handles-returns",
  };
  const id = text.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  return sectionIdMap[id] || id;
};

// Helper component to render markdown with proper section wrapping
const MarkdownRenderer = ({ content, onRenderComplete }) => {
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current && onRenderComplete) {
      // Wait for ReactMarkdown to finish rendering
      const timeoutId = setTimeout(() => {
        onRenderComplete();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [content, onRenderComplete]);

  // Process markdown to split into sections
  const processedSections = useMemo(() => {
    if (!content) return [];

    const lines = content.split('\n');
    const sections = [];
    let currentSection = null;
    let currentContent = [];

    lines.forEach((line) => {
      if (line.startsWith('# ')) {
        // h1 - add as standalone
        if (currentSection) {
          sections.push({ ...currentSection, content: currentContent.join('\n') });
          currentSection = null;
          currentContent = [];
        }
        sections.push({ type: 'h1', content: line });
      } else if (line.startsWith('## ')) {
        // h2 - start new section
        if (currentSection) {
          sections.push({ ...currentSection, content: currentContent.join('\n') });
        }
        const title = line.replace('## ', '').trim();
        currentSection = {
          type: 'h2',
          id: getSectionId(title),
          title: line,
        };
        currentContent = [];
      } else if (line.startsWith('### ')) {
        // h3 - start new section
        if (currentSection) {
          sections.push({ ...currentSection, content: currentContent.join('\n') });
        }
        const title = line.replace('### ', '').trim();
        currentSection = {
          type: 'h3',
          id: getSectionId(title),
          title: line,
        };
        currentContent = [];
      } else {
        // Regular content - add to current section
        if (currentSection) {
          currentContent.push(line);
        } else {
          // Content before first section
          if (sections.length === 0 || sections[sections.length - 1].type === 'h1') {
            if (sections.length > 0 && sections[sections.length - 1].type === 'h1') {
              sections[sections.length - 1].content += '\n' + line;
            }
          }
        }
      }
    });

    // Add last section
    if (currentSection) {
      sections.push({ ...currentSection, content: currentContent.join('\n') });
    }

    return sections;
  }, [content]);

  return (
    <div ref={contentRef}>
      {processedSections.map((section, index) => {
        if (section.type === 'h1') {
          return (
            <ReactMarkdown key={`h1-${index}`} components={{
              h1: ({ children, ...props }) => <h1 {...props}>{children}</h1>,
            }}>
              {section.content}
            </ReactMarkdown>
          );
        }

        return (
          <section key={section.id || `section-${index}`} id={section.id} className="section">
            <ReactMarkdown
              components={{
                h2: ({ children, ...props }) => <h2 {...props}>{children}</h2>,
                h3: ({ children, ...props }) => <h3 {...props}>{children}</h3>,
                p: ({ children, ...props }) => <p {...props}>{children}</p>,
                strong: ({ children, ...props }) => <strong {...props}>{children}</strong>,
                ul: ({ children, ...props }) => <ul {...props}>{children}</ul>,
                ol: ({ children, ...props }) => <ol {...props}>{children}</ol>,
                li: ({ children, ...props }) => <li {...props}>{children}</li>,
                a: ({ children, ...props }) => (
                  <a {...props} style={{ color: "#ae0000" }} rel="noopener noreferrer">
                    {children}
                  </a>
                ),
              }}
            >
              {section.title + '\n' + section.content}
            </ReactMarkdown>
          </section>
        );
      })}
    </div>
  );
};

const ReturnPolicy = () => {
  const [activeSection, setActiveSection] = useState("return-eligibility-timeframe");
  const [markdownContent, setMarkdownContent] = useState("");
  const sectionRefs = useRef({});
  const sidebarRef = useRef(null);

  const sections = useMemo(() => [
    { id: "return-eligibility-timeframe", title: "Return Eligibility & Timeframe" },
    { id: "return-shipping-costs", title: "Return Shipping Costs" },
    { id: "restocking-fees", title: "Restocking Fees" },
    { id: "damaged-or-incorrect-items", title: "Damaged or Incorrect Items" },
    { id: "final-sale-custom-orders", title: "Final Sale & Custom Orders" },
    { id: "refund-processing", title: "Refund Processing" },
    { id: "who-handles-returns", title: "Who Handles Returns?" },
  ], []);

  // Load markdown file
  useEffect(() => {
    fetch("/return-policy.md")
      .then((response) => response.text())
      .then((text) => setMarkdownContent(text))
      .catch((error) => {
        console.error("Error loading markdown file:", error);
      });
  }, []);

  const handleMarkdownRender = useCallback(() => {
    // Create refs for each section after markdown is rendered
    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        sectionRefs.current[section.id] = element;
      }
    });
  }, [sections]);

  useEffect(() => {
    // Only set up scroll handler after markdown is rendered and sections are set up
    if (!markdownContent) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // Offset for sticky header

      // Find which section is currently in view
      let currentSection = sections[0]?.id || "return-eligibility-timeframe"; // Default to first section

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = sectionRefs.current[section.id];
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          // If we've scrolled past the top of this section, it's the active one
          if (scrollPosition >= elementTop - 100) {
            currentSection = section.id;
            break;
          }
        }
      }

      setActiveSection(currentSection);
    };

    // Initial check after a delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      handleScroll();
    }, 500);

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [sections, markdownContent]);

  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      const offsetTop = element.offsetTop - 100; // Offset for sticky header
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <ReturnPolicyStyle>
      <SEO
        title="Return & Refund Policy - Instabuild | Canada's Leading Building Materials Platform"
        description="Learn about Instabuild's return and refund policy. Understand return eligibility, shipping costs, restocking fees, and how to handle damaged or incorrect items."
        keywords="return policy, refund policy, return and refund, return eligibility, restocking fees, Instabuild returns"
      />
      <div className="container">
        <aside className="sidebar" ref={sidebarRef}>
          {sections.map((section) => (
            <div
              key={section.id}
              className={`sidebar__item ${activeSection === section.id ? "active" : ""}`}
              onClick={() => scrollToSection(section.id)}
            >
              {section.title}
            </div>
          ))}
        </aside>
        <div className="content">
          {markdownContent ? (
            <MarkdownRenderer content={markdownContent} onRenderComplete={handleMarkdownRender} />
          ) : (
            <div>Loading...</div>
          )}
        </div>
      </div>
    </ReturnPolicyStyle>
  );
};

export default ReturnPolicy;
